/* eslint-disable no-console */
import { useState } from 'react';
import { useConfigService } from '@thd-nucleus/experience-context';
import { authService } from '../services/auth-service';
import { getTimestampFromJWT, processAccountActivityEvents } from '../utils/auth-utils';
import {
  FEATURE_SWITCH_NAMES,
  DEFAULT_AUTH_EVENT_FILTER_HOURS,
} from '../utils/consts';
import { parseEventIdsFromConfig } from '../utils/utils';
import { IAuthenticationEvent } from '../models/common-models';

interface UseAuthActivityReturn {
  matchedAuthEvent: IAuthenticationEvent | null;
  isAccountActivityLoading: boolean;
  fetchUserAuthActivity: (jwt: string, sessionId: string) => Promise<void>;
  clearAuthEvent: () => void;
}

export const useAuthActivity = (): UseAuthActivityReturn => {
  const [matchedAuthEvent, setMatchedAuthEvent] = useState<IAuthenticationEvent | null>(null);
  const [isAccountActivityLoading, setIsAccountActivityLoading] = useState<boolean>(false);

  // Get configurable values from config service
  const authEventFilterHoursConfig = useConfigService(
    FEATURE_SWITCH_NAMES.AUTH_EVENT_FILTER_HOURS
  ) as string | undefined;
  // Parse config values
  const authEventFilterHours = parseInt(
    authEventFilterHoursConfig || DEFAULT_AUTH_EVENT_FILTER_HOURS.toString(),
    10
  );
  const signInEventIdsConfig = useConfigService(
    FEATURE_SWITCH_NAMES.S2P_SIGN_IN_EVENTS
  ) as string | undefined;
  const otpEventIdsConfig = useConfigService(
    FEATURE_SWITCH_NAMES.S2P_OTP_EVENTS
  ) as string | undefined;

  const signInEventIds = parseEventIdsFromConfig(signInEventIdsConfig);
  const otpEventIds = parseEventIdsFromConfig(otpEventIdsConfig);

  const fetchUserAuthActivity = async (jwtToken: string, sessionId: string) => {
    if (!jwtToken) {
      console.error('fetchUserAuthActivity: Cannot fetch user auth activity - JWT token is missing or null');
      return;
    }
    if (!sessionId) {
      console.error('fetchUserAuthActivity: Cannot fetch user auth activity - Session ID is missing or invalid');
      return;
    }

    setIsAccountActivityLoading(true);

    try {
      const result = await authService.fetchUserAuthActivity(jwtToken);
      if (result.success && result.data) {
        // Decode JWT token on successful auth activity fetch
        const decodedJWT = getTimestampFromJWT(jwtToken);

        if (decodedJWT) {
          const matchedEvent = processAccountActivityEvents(
            result.data.events,
            decodedJWT,
            authEventFilterHours,
            signInEventIds,
            otpEventIds,
            sessionId
          );

          setMatchedAuthEvent(matchedEvent);
        } else {
          console.error('Failed to extract timestamp from JWT');
          setMatchedAuthEvent(null);
        }
      } else {
        setMatchedAuthEvent(null);
      }
    } catch (err) {
      console.error('fetchUserAuthActivity: Failed to fetch user auth activity:', err);
      setMatchedAuthEvent(null);
    } finally {
      setIsAccountActivityLoading(false);
    }
  };

  const clearAuthEvent = () => {
    setMatchedAuthEvent(null);
  };

  return {
    matchedAuthEvent,
    isAccountActivityLoading,
    fetchUserAuthActivity,
    clearAuthEvent,
  };
};
